import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, ToggleLeft, ToggleRight, Save, Loader as Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import type { CollectionSettings } from '../../hooks/useSalesHistory';

interface CollectionSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: CollectionSettings | null;
  onSave: (settings: Partial<CollectionSettings>) => Promise<boolean>;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

function getNextCollectionDate(days: string[], time: string): string | null {
  if (days.length === 0) return null;
  const now = new Date();
  const dayMap: Record<string, number> = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
  };

  const targetDays = days.map(d => dayMap[d]).filter(d => d !== undefined).sort((a, b) => a - b);
  if (targetDays.length === 0) return null;

  const currentDay = now.getDay();
  const [hours, minutes] = time.split(':').map(Number);

  for (let offset = 0; offset <= 7; offset++) {
    const checkDay = (currentDay + offset) % 7;
    if (targetDays.includes(checkDay)) {
      const date = new Date(now);
      date.setDate(now.getDate() + offset);
      date.setHours(hours, minutes, 0, 0);
      if (date > now) {
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }
  }

  const nextDay = targetDays[0];
  const diff = (nextDay + 7 - currentDay) % 7 || 7;
  const date = new Date(now);
  date.setDate(now.getDate() + diff);
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const CollectionSettingsModal: React.FC<CollectionSettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>(['tuesday', 'friday']);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setIsEnabled(settings.is_enabled);
      setSelectedDays(settings.collection_days || ['tuesday', 'friday']);
      setSelectedTime(settings.collection_time || '09:00');
    }
  }, [settings]);

  if (!isOpen) return null;

  const toggleDay = (day: string) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      return [...prev, day];
    });
  };

  const handleSave = async () => {
    if (isEnabled && selectedDays.length === 0) return;
    setSaving(true);
    const success = await onSave({
      is_enabled: isEnabled,
      collection_days: selectedDays,
      collection_time: selectedTime,
    });
    setSaving(false);
    if (success) onClose();
  };

  const nextCollection = isEnabled ? getNextCollectionDate(selectedDays, selectedTime) : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Collection Schedule
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Auto-reserve inventory on schedule
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Automatic Reservations
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Create reservations on collection days
              </p>
            </div>
            <button
              onClick={() => setIsEnabled(!isEnabled)}
              className="focus:outline-none"
            >
              {isEnabled ? (
                <ToggleRight className="h-8 w-8 text-teal-600 dark:text-teal-400" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-gray-400" />
              )}
            </button>
          </div>

          <div className={`space-y-5 transition-opacity ${isEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Collection Days
              </label>
              <div className="flex gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <button
                    key={day.key}
                    onClick={() => toggleDay(day.key)}
                    className={`flex-1 py-2.5 px-1 rounded-lg text-xs font-semibold transition-all ${
                      selectedDays.includes(day.key)
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              {isEnabled && selectedDays.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Select at least one day or automatic reservations will never run
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Clock className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Collection Time (UTC)
              </label>
              <select
                value={selectedTime}
                onChange={e => setSelectedTime(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {TIME_OPTIONS.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {nextCollection && (
              <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4">
                <p className="text-xs font-medium text-teal-700 dark:text-teal-300 uppercase tracking-wider mb-1">
                  Next Collection
                </p>
                <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">
                  {nextCollection}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            loading={saving}
            disabled={isEnabled && selectedDays.length === 0}
          >
            <Save className="h-4 w-4 mr-1.5" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};
